<template>
  <div class="BeneficiaryForm">
    <h2 class="gl_form-title small mb-5">
      {{ t('copy_life_beneficiary-form_title') }}
    </h2>

    <p class="gl__subtitle-light">{{ t('copy_life_beneficiary-form_subtitle') }}</p>

    <FormField
      class="mb-8"
      :statement="t('copy_life_beneficiary-form_people-question')"
    >
      <PlusMinusComponent
        @decrease="removeBeneficiary"
        @increase="addBeneficiary"
        :decrease-disabled="beneficiaries.length <= 1"
        :increase-disabled="beneficiaries.length >= MAX_BENEFICIARIES"
        :value="beneficiariesSize"
      />
    </FormField>

    <div
      class="beneficiary mb-3"
      v-for="(beneficiary, i) in beneficiaries"
      :key="i"
    >
      <UserCardForm :title="t('copy_life_beneficiary-form_beneficiary', { index: i + 1 })" />

      <BaseSelect
        :id="`documentType-${i}`"
        :label="t('copy_form-input_document-type')"
        v-model="beneficiary.documentType"
        :items="documents"
        v-on:update:model-value="onDocumentChange(beneficiary, $event)"
      />

      <BaseInput
        :id="`document-number-${i}`"
        :key="beneficiary.documentType"
        :label="t('copy_form-input_document-number')"
        :placeholder="getDocumentNumberPlaceholder(beneficiary.documentType)"
        icon="icon-card-01"
        v-model="beneficiary.documentNumber"
        :rules="getDocumentNumberRules(beneficiary.documentType)"
        :editable="beneficiary.documentType !== 'MENOR'"
      />

      <BaseInput
        :id="`name-${i}`"
        :label="t('copy_form-input_name')"
        :placeholder="t('copy_form-input_name-placeholder')"
        v-model="beneficiary.name"
        :rules="[required, validName]"
        :maxlength="50"
        :uppercase="true"
      />

      <BaseInput
        :id="`surname1-${i}`"
        :label="t('copy_form-input_first-surname')"
        :placeholder="t('copy_form-input_first-surname-placeholder')"
        v-model="beneficiary.firstSurname"
        :rules="[required, validName]"
        :maxlength="50"
        :uppercase="true"
      />

      <BaseInput
        :id="`surname2-${i}`"
        :label="t('copy_form-input_second-surname')"
        :placeholder="t('copy_form-input_second-surname-placeholder')"
        v-model="beneficiary.secondSurname"
        :rules="getSurnameRules(beneficiary.documentType)"
        :maxlength="50"
        :uppercase="true"
      />
    </div>

    <BaseCheckBox
      id="declaration"
      :label="t('hiring.common.insurance-another-company.check-label')"
      :value="declaration"
      v-model="declaration"
      :rules="[validationRules.checkRequired]"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, onBeforeMount, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCommonStore } from '@/modules/core/stores/commonStore'
import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { useRules } from '@/modules/core/composables/useRulesComposable'
import { MAX_CHARS_PASSPORT } from '@/modules/core/constants/common'
import type { OptionsValuesInterface } from '@/modules/core/models/types'
import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
import type LifeHiringAnswers from '@/modules/life/hiring/models/APP/LifeHiringAnswers'
import LifeBeneficiary from '../../models/APP/LifeBeneficiary'
import { UserRoleEnum } from '@/modules/core/models/enums'
import PlusMinusComponent from '@/modules/core/components/utils/PlusMinusComponent.vue'
import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
import UserCardForm from '@/modules/health/hiring/components/UserCardForm.vue'
import FormField from '@/modules/core/components/utils/FormField.vue'
import BaseCheckBox from '@/modules/core/components/ui/BaseCheckBox.vue'
import LifeInsuranceProcessStepEnum from '@/modules/life/hiring/models/APP/LifeInsuranceProcessStepEnum'

const MAX_BENEFICIARIES = 100

const { t } = useI18n()
const commonStore = useCommonStore()
const loadingStore = useLoadingStore()
const { required, validName, validCIF, validNIE, validNIF, validPassport } = useRules()

defineExpose({ triggerSubmit })

interface PropsInterface {
  nextForm: String[]
  answers: LifeHiringAnswers
  configurations: LifeConfigurations
}
const props = withDefaults(defineProps<PropsInterface>(), {})

const validationRules = {
  required,
  validNIF,
  validNIE,
  validCIF,
  checkRequired: (value: boolean) => required(value, 'common.validations.checked'),
  characterLimit: (value: string) =>
    value.length === MAX_CHARS_PASSPORT || t('common.validations.passport-error-not-holder'),
  validPassport: (value: string) => validPassport(value, 'common.validations.invalid-characters'),
  isNumberDuplicated: (value: string) => {
    const documentNumbers = beneficiaries.value.filter((b) => b.documentNumber === value)
    return documentNumbers.length === 1 || t('common.validations.card-repeat')
  }
}

const DOCUMENTS: Record<string, any> = Object.freeze({
  NIF: [validationRules.required, validationRules.validNIF, validationRules.isNumberDuplicated],
  NIE: [validationRules.required, validationRules.validNIE, validationRules.isNumberDuplicated],
  PAS: [
    validationRules.required,
    validationRules.validPassport,
    validationRules.characterLimit,
    validationRules.isNumberDuplicated
  ],
  TRE: [validationRules.required, validationRules.isNumberDuplicated],
  CIF: [validationRules.required, validationRules.validCIF, validationRules.isNumberDuplicated],
  CIE: [validationRules.required, validationRules.isNumberDuplicated]
})

const documents = ref<OptionsValuesInterface[]>([])
const beneficiaries = ref<LifeBeneficiary[]>(getInitBeneficiaries())
const declaration = ref<boolean>(getInitDeclaration())

const beneficiariesSize = computed(() => {
  const size = beneficiaries.value.length
  return t('copy_life_beneficiary-form_people', { size }, size)
})

function removeBeneficiary() {
  if (beneficiaries.value.length <= 1) return

  beneficiaries.value.pop()
}
function addBeneficiary() {
  if (beneficiaries.value.length >= MAX_BENEFICIARIES) return

  const beneficiaryRiskNumber = beneficiaries.value.length + 1
  const newBeneficiary = new LifeBeneficiary({ beneficiaryRiskNumber })
  beneficiaries.value.push(newBeneficiary)
}

function getDocumentNumberRules(documentType: string) {
  return DOCUMENTS[documentType]
}

function getSurnameRules(documentType: string) {
  return documentType === 'NIF' ? [required, validName] : [validName]
}

function getDocumentNumberPlaceholder(documentType: string) {
  return documents.value.find((d) => d.value === documentType)?.label
}

function getInitBeneficiaries() {
  const insured = props.answers.insureds[0]
  return insured.beneficiaries ?? [new LifeBeneficiary({ beneficiaryRiskNumber: 1 })]
}

function getInitDeclaration() {
  const { stepCode } = props.answers.processStatus
  if (!stepCode) return false

  return stepCode > LifeInsuranceProcessStepEnum.BENEFICIARIES
}

function triggerSubmit() {
  const page = props.nextForm[0]

  const insureds = props.answers.insureds
  insureds[0].beneficiaries = beneficiaries.value
  const answers = { insureds }

  return { page, answers }
}

async function fetchDocumentTypes() {
  const documents = await commonStore.fetchDocumentsByRole(UserRoleEnum.INSURED)

  return documents.map((d) => ({
    label: d.documentDescription,
    value: d.documentType
  }))
}

async function beforeMount() {
  try {
    loadingStore.startLoading()

    documents.value = await fetchDocumentTypes()
  } catch (error) {
    console.error(error)
  } finally {
    loadingStore.stopLoading()
  }
}

function onDocumentChange(beneficiary: LifeBeneficiary, documentType: string) {
  beneficiary.documentNumber = ''
  beneficiary.documentTypeText = getDocumentNumberPlaceholder(documentType)!
}

onBeforeMount(beforeMount)
</script>
